import axiosInstance from './axios';
import { parseCommerce } from './commerceServices';
import PaginationData from 'types/models/PaginationData';
import Order, { Payment } from 'types/models/Order';
import OrderDetail from 'types/models/OrderDetail';
import { ConsumptionReportPayloadData } from 'types/forms/ConsumptionsReportFormProps';
import { FormDataTotalOrders, ProtocolOrderPayloadData, VenueAndCommerce } from 'types/forms/ProtocolOrdersFormProps';
import BalanceTransaction from 'types/models/BalanceTransaction';
import ClothingSizeType from 'types/models/ClothingSizeType';
import { parseUser } from './authServices';
import { parseLuxuryBox, parseVenue } from './venuesServices';
import { parseEvent } from './eventServices';
import { parseFDPayment, parseCloverPointPayment, parseFiservQrPayment, parseIzipayPayment } from './paymentsServices';
import { parseCloverPoint } from './cloverServices';
import BillingByVenue, { BestSellingProduct, CommerceTotalSale, SalesByCommerce } from 'types/models/MetricData';
import { parseProduct } from './productServices';

export const getReportingOrdersTotal = async (formData: ConsumptionReportPayloadData): Promise<number | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `reporting/orders/total`,
      data: formData,
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getMetricBillingTotal = async (body: any): Promise<BillingByVenue[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `metrics/billing-by-venues`,
      data: body,
    });
    return parseBillingByVenueData(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getMetricSalesByCommerce = async (body: any): Promise<SalesByCommerce | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `metrics/sales-by-commerces`,
      data: body,
    });
    return parseSalesByCommerces(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

const parseSalesByCommerces = (data: any) => ({
  totalSales: data.totalSales ? parseTotalSales(data.totalSales) : [],
  bestSellingProducts: parseBestSellingProduct(data.bestSellingProducts),
});

const parseBestSellingProduct = (data: any[]): BestSellingProduct[] => {
  return data.map((item) => ({
    commerceId: item.id,
    commerceName: item.name,
    productName: item.product,
    totalSale: parseFloat(item.total_vendido),
  }));
};

const parseTotalSales = (data: any[]): CommerceTotalSale[] => {
  return data.map((item) => ({
    id: item.id,
    name: item.name,
    totalSales: item.cantidad_ventas,
  }));
};

const parseBillingByVenueData = (data: any[]) => {
  return data.map(
    (item: any): BillingByVenue => ({
      venueId: item.venueId,
      totalBilling: item.total_billing,
      commerces: item.commerces.map((commerce: any) => ({
        commerceId: commerce.commerceId,
        billing: commerce.billing,
      })),
    }),
  );
};

export const getReportingOrdersSummary = async (formData: ConsumptionReportPayloadData): Promise<any> => {
  try {
    const response = await axiosInstance({
      method: 'post',
      url: `reporting/summary`,
      data: {
        ...formData,
      },
    });
    return response.data; // Retorna { total_transactions, total_amount }
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getReportingOrders = async (
  formData: ConsumptionReportPayloadData,
  page?: number,
): Promise<PaginationData<Order> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'post',
      url: `reporting/orders?page=${page ?? 1}`,
      data: {
        ...formData,
      },
    });
    return {
      currentPage: paginationData.current_page,
      data: parseOrders(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const downloadInvoice = async (orderId: number): Promise<void> => {
  try {
    const response = await axiosInstance({
      method: 'get',
      url: `orders/${orderId}/stream-invoice`,
      responseType: 'blob', // IMPRESCINDIBLE: indica a Axios que trate la respuesta como binario
    });

    // 1. Crear un Blob a partir de los datos recibidos
    const blob = new Blob([response.data], { type: 'application/pdf' });

    // 2. Generar una URL temporal en el navegador para el Blob
    const downloadUrl = window.URL.createObjectURL(blob);

    // 3. Crear un elemento <a> dinámico invisible
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `Factura-Orden-${orderId}.pdf`);

    // 4. Agregar al DOM, simular el clic y limpiar
    document.body.appendChild(link);
    link.click();

    // Limpieza de memoria
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error descargando la factura:', error);
  }
};

export const getAllReportingOrders = async (
  formData: ConsumptionReportPayloadData,
  lang: string,
): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `reporting/orders`,
      data: {
        ...formData,
        download: true,
        lang,
      },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getReportingLoads = async (
  params: Record<any, any>,
  page?: number,
): Promise<PaginationData<BalanceTransaction> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'post',
      url: `reporting/loads?page=${page ?? 1}`,
      data: {
        ...params,
        isToDownload: false,
      },
    });
    return {
      currentPage: paginationData.current_page,
      data: parseBalanceTransactions(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAllReportingLoads = async (params: Record<any, any>, lang: string): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `reporting/loads`,
      data: {
        ...params,
        isToDownload: true,
        lang,
      },
      responseType: 'blob',
    });

    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const storeOrder = async (formData: ProtocolOrderPayloadData): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'POST',
      url: 'orders',
      data: { ...formData },
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const setOrdersToBilling = async (formData: ProtocolOrderPayloadData): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'PUT',
      url: 'orders/set-orders-to-billing',
      data: { ...formData },
    });
    return data;
  } catch (error: any) {
    console.log(error);
    return Promise.reject(error.response.data);
  }
};

export const getProtocolOrders = async (
  form: VenueAndCommerce,
  page?: number,
): Promise<PaginationData<Order> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'POST',
      url: `protocol-orders?page=${page ?? 1}`,
      data: form,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseOrders(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const getProtocolOrdersTotal = async (formData: FormDataTotalOrders): Promise<number | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `protocol-orders/total`,
      data: formData,
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const parseOrders = (orders: any[]): Order[] => {
  return orders.map((order): Order => parseOrder(order));
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const parseOrder = (order: any): Order => {
  return {
    id: order.id,
    total: order.total_amount,
    orderStatus: order.order_status_id,
    isCash: order.is_cash === 1,
    isCourtesy: order.is_courtesy === 1,
    isPreOrder: order.is_pre_order,
    guestEmail: order.guest_email,
    firstDataPayment: order?.payment?.first_data_payment ? parseFDPayment(order.payment.first_data_payment) : null,
    refundOfOrder: order.refund_of_order,
    cloverDataPayment: order?.payment?.clover_point_payment
      ? parseCloverPointPayment(order?.payment?.clover_point_payment)
      : null,
    fiservQrDataPayment: order?.payment?.fiserv_qr_subsidiary_sales_stand_payment
      ? parseFiservQrPayment(order?.payment?.fiserv_qr_subsidiary_sales_stand_payment)
      : null,
    izipayPayment: order?.payment?.izipay_payment ? parseIzipayPayment(order?.payment?.izipay_payment) : null,
    checkingAccountPayment: order?.payment?.checking_account_payment
      ? {
          device: parseCloverPoint(order?.payment?.checking_account_payment.device),
        }
      : null,
    luxuryBox: order?.luxury_box ? parseLuxuryBox(order?.luxury_box) : undefined,
    commerce: parseCommerce(order.commerce),
    event: order.event ? parseEvent(order.event) : undefined,
    phone: order.phone,
    createdAt: order.created_at,
    updatedAt: order.update_at,
    localDatetime: order.local_datetime,
    details: order.order_detail
      ? parseDetailsOrder(order.order_detail)
      : order.order_details
      ? parseDetailsOrder(order.order_details)
      : [],
    delivery: order.delivery,
    billingStatusId: order.billing_status_id,
    invoiceProcessId: order.invoice_process_id,
    user: order.user ? parseUser(order.user) : undefined,
    payment: order.payment ? parsePayment(order.payment) : undefined,
  };
};

const parsePayment = (payment: any): Payment => ({
  id: payment.id,
  createdAt: payment.created_at,
  isCash: payment.is_cash == 1,
  isBalance: payment.is_balance == 1,
  isCourtesy: payment.is_courtesy === 1,
  isSplitPayment: payment.is_split_payment === 1,
  cloverPointPaymentId: payment.clover_point_payment_id,
  partialAmount: payment.partial_amount,
  fiservQrPayment: payment.fiserv_qr_subsidiary_sales_stand_payment
    ? parseFiservQrPayment(payment.fiserv_qr_subsidiary_sales_stand_payment)
    : undefined,
  cloverPointPayment: payment.clover_point_payment ? parseCloverPointPayment(payment.clover_point_payment) : undefined,
  subPayments: payment.sub_payments ? parsePayments(payment.sub_payments) : [],
});

const parsePayments = (payments: any[]) => payments.map((item) => parsePayment(item));

const parseDetailsOrder = (details: []): OrderDetail[] => {
  return details.map((detail): OrderDetail => parseDetails(detail));
};

const parseDetails = (detail: any): OrderDetail => ({
  id: detail.id,
  product: detail.product,
  price: detail.price,
  estimatedPreparationTime: detail.estimated_preparation_time,
  quantity: detail.quantity,
  totalAmount: detail.total_amount,
  comments: detail.comments,
  clothinSizeType: detail.clothing_size_type_id ? parseClothingSizeType(detail.clothing_size_type) : undefined,
  fullProduct: detail.product ? parseProduct(detail.product) : undefined,
  tomorrowQuantityBenefit: detail.tomorrow_benefit_quantity,
  tomorrowBenefitDiscountPercentage: detail.tomorrow_benefit_discount_percentage,
});

const parseClothingSizeType = (clothingSize: any): ClothingSizeType => ({
  id: clothingSize.id,
  description: clothingSize.description,
});

const parseBalanceTransactions = (balances: any[]): BalanceTransaction[] => {
  return balances.map((balance): BalanceTransaction => parseBalanceTransaction(balance));
};

const parseBalanceTransaction = (order: any): BalanceTransaction => {
  return {
    id: order.id,
    amount: Number(order.amount),
    createdAt: order.created_at,
    localDateTime: order.local_datetime,
    user: parseUser(order.user),
    venue: parseVenue(order.venue),
  };
};
